import { useEffect, useState } from 'react';

const KEYBOARD_DETECTION_THRESHOLD = 150; // Minimum height difference to detect virtual keyboard (px)
const DEFAULT_HEADER_HEIGHT = 61; // Default header height when keyboard is not active (px)

interface VirtualKeyboardState {
  isVisible: boolean;
  height: number;
}

/**
 * Hook to detect whether the virtual keyboard is currently active on mobile devices
 * Uses Visual Viewport API for more accurate detection
 */
export const useVirtualKeyboard = (): VirtualKeyboardState => {
  const [keyboardState, setKeyboardState] = useState<VirtualKeyboardState>({
    isVisible: false,
    height: 0,
  });

  useEffect(() => {
    if (!window.visualViewport) {
      return;
    }

    const windowHeight = window.innerHeight;

    const handleViewportChange = () => {
      if (!window.visualViewport) {
        return;
      }

      const currentViewportHeight = window.visualViewport.height;
      const heightDifference = windowHeight - currentViewportHeight;

      // Threshold to detect keyboard presence
      const isKeyboardVisible = heightDifference > KEYBOARD_DETECTION_THRESHOLD;

      setKeyboardState({
        isVisible: isKeyboardVisible,
        height: isKeyboardVisible ? heightDifference : 0,
      });
    };

    window.visualViewport.addEventListener('resize', handleViewportChange);

    const handleFocusIn = (event: FocusEvent) => {
      const target = event.target as HTMLElement;
      if (
        target &&
        (target.tagName === 'INPUT' ||
          target.tagName === 'TEXTAREA' ||
          target.contentEditable === 'true')
      ) {
        setTimeout(handleViewportChange, 300);
      }
    };

    const handleFocusOut = () => {
      setTimeout(handleViewportChange, 300);
    };

    document.addEventListener('focusin', handleFocusIn);
    document.addEventListener('focusout', handleFocusOut);

    handleViewportChange();

    return () => {
      if (window.visualViewport) {
        window.visualViewport.removeEventListener('resize', handleViewportChange);
      }
      document.removeEventListener('focusin', handleFocusIn);
      document.removeEventListener('focusout', handleFocusOut);
    };
  }, []);

  return keyboardState;
};

/**
 * Hook to get dynamic header height
 * Returns 0 when virtual keyboard is active on mobile, DEFAULT_HEADER_HEIGHT when inactive
 */
export const useDynamicHeaderHeight = (): number => {
  const { isVisible: isVirtualKeyboardVisible } = useVirtualKeyboard();
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    checkMobile();
    window.addEventListener('resize', checkMobile);

    return () => {
      window.removeEventListener('resize', checkMobile);
    };
  }, []);

  return isMobile && isVirtualKeyboardVisible ? 0 : DEFAULT_HEADER_HEIGHT;
};
